import React from "react";
import { useWriteAccess, type UseWriteAccessResult, type WriteArea } from "../../hooks/useWriteAccess";

/**
 * CO-24 — class-component adapter for `useWriteAccess`.
 *
 * Several of the oldest and highest-traffic write surfaces (the employees list, `add-user`) are
 * still class components, so they cannot call the hook. Rather than duplicate the allowlist logic
 * for them — which is exactly the drift this task exists to prevent — they receive the SAME hook
 * result as a `writeAccess` prop.
 *
 *   export default withWriteAccess(connect(...)(MyClassView));
 */
export interface WithWriteAccessProps {
  writeAccess: UseWriteAccessResult;
}

export function withWriteAccess<P extends WithWriteAccessProps>(
  Wrapped: React.ComponentType<P>,
  area: WriteArea = "gated",
): React.FC<Omit<P, "writeAccess">> {
  const WithWriteAccess: React.FC<Omit<P, "writeAccess">> = (props) => {
    const writeAccess = useWriteAccess(area);
    return <Wrapped {...(props as P)} writeAccess={writeAccess} />;
  };
  WithWriteAccess.displayName = `withWriteAccess(${Wrapped.displayName || Wrapped.name || "Component"})`;
  return WithWriteAccess;
}

export default withWriteAccess;
